import { RunbookInterruptPayload, RunbookResponse, RunbookResumeMessage, v2 } from "@pufflig/prompt-studio-types";
import { safeStringify } from "@pufflig/prompt-tools";
import { createProcessRecord, createSandbox, SandboxEvent, SandboxRunOptions } from "@pufflig/promptbook-code-sandbox";
import { SQLDatabase } from "@pufflig/promptbook-db";
import { PromptbookEditorRef } from "@pufflig/promptbook-editor";
import { createHostAPI } from "../../sandbox/createHostAPI";

const handleCodeRunInterrupt = async (
  threadId: string,
  interrupt: RunbookInterruptPayload,
  db: SQLDatabase,
  connection: WebSocket,
  ref: React.RefObject<PromptbookEditorRef>,
) => {
  const code = interrupt.args.code;
  let context = interrupt.context || "{}";

  const onStateUpdate = (payload: SandboxEvent) => {
    const runbookStepId = threadId.split("_")[0];

    const patch: v2.RunState = {
      [runbookStepId]: {
        status: "running",
        process: { [payload.id]: createProcessRecord(payload) },
      } as any,
    };

    ref.current?.handleStateUpdate(patch, { threadId, isDone: false });
  };

  const options: SandboxRunOptions = {
    cache: {},
    mode: "cache",
    semanticModel: context,
    variables: {},
    onStateUpdate,
    // add onCacheUpdate to handle cache updates
  };

  const sandbox = await createSandbox(createHostAPI({ db, connection }), options);
  const { success, error } = await sandbox.run(code);

  const parsed = JSON.parse(success || "null");
  const results = parsed?.results;

  const payload: RunbookResumeMessage = {
    type: "runbook_resume",
    threadId,
    toolResult: { success, error },
    runbook: interrupt.runbook,
  };

  connection.send(safeStringify(payload));

  return { toolResult: { success, error }, results, error };
};

/**
 * The backend can request an interrupt for different reasons.
 * Right now, we only support the run_code interrupt. This interrupt will run code on the client.
 *
 * @param interrupt
 * @param runtime
 * @param connection
 * @param onStateUpdate
 * @returns
 */
export const handleRunbookInterrupt = async (
  parsedData: RunbookResponse,
  db: SQLDatabase,
  connection: WebSocket,
  ref: React.RefObject<PromptbookEditorRef>,
) => {
  if (parsedData.interrupt?.client_tool === "run_code") {
    return handleCodeRunInterrupt(parsedData.threadId, parsedData.interrupt, db, connection, ref);
  }

  return { toolResult: null, error: "Unknown interrupt type" };
};
